import { buildMultiFallbackContainerHtml } from './cpa-grid-bridge-format.utils';

/**
 * Bridge mínimo pro contexto AngularJS que o fallback de select single precisa consultar
 * (scope/ng mudam de instância ao longo do ciclo de vida do CpaGridBridgeComponent — daí os
 * getters em vez de valores fixos no construtor).
 */
export interface CpaSelectFallbackAngularJsContext {
  getScope(): any;
  getNg(): any;
}

/**
 * Renderiza os fallbacks de select (single e multiple) usados quando select2/jQuery não está
 * disponível no bundle (cenário real da aplicação). Usa portais em document.body, listeners
 * globais de document.click, e idempotência via atributo marcador no select/container.
 */
export class CpaSelectFallbackController {
  private readonly portalMenus: HTMLElement[] = [];
  private readonly documentClickHandlers: Array<(e: Event) => void> = [];

  constructor(private readonly angularJs: CpaSelectFallbackAngularJsContext) {}

  renderSingleSelectFallback(hostEl: HTMLElement | null | undefined): void {
    if (!hostEl) {
      return;
    }

    const jq = (window as any).$ || (window as any).jQuery;
    const hasSelect2 = !!(jq && jq.fn && typeof jq.fn.select2 === 'function');
    if (hasSelect2) {
      return;
    }

    const singleSelects = hostEl.querySelectorAll('select[bf-select2]:not([multiple]), select[data-bf-select2]:not([multiple])');
    singleSelects.forEach((selectEl) => {
      const select = selectEl as HTMLSelectElement;
      if ((select as any).__cpaSingleFallbackRendered) {
        return;
      }

      const gridsterItem = select.closest('.gridster-item') as HTMLElement | null;

      const wrapper = document.createElement('div');
      wrapper.className = 'cpa-single-select-fallback';

      const trigger = document.createElement('button');
      trigger.type = 'button';
      trigger.className = 'cpa-single-select-trigger';

      const updateTriggerText = () => {
        const selectedOption = select.options[select.selectedIndex];
        const text = selectedOption ? (selectedOption.textContent || '').trim() : '';
        trigger.textContent = text || 'Selecionar';
        trigger.classList.toggle('cpa-single-select-trigger--placeholder', !select.value);
      };
      updateTriggerText();

      // Menu é portado para document.body com position:fixed para escapar de
      // qualquer overflow:hidden/auto nos containers pais (ex: modal com scroll).
      const menu = document.createElement('div');
      menu.className = 'cpa-single-select-menu-portal';

      const renderOptions = () => {
        menu.innerHTML = '';
        Array.from(select.options).forEach((opt) => {
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'cpa-single-select-option';
          btn.textContent = (opt.textContent || '').trim();
          btn.setAttribute('data-value', opt.value);
          if (opt.value === select.value) {
            btn.classList.add('is-selected');
          }
          menu.appendChild(btn);
        });
      };
      renderOptions();

      document.body.appendChild(menu);
      this.portalMenus.push(menu);

      select.style.display = 'none';
      select.parentNode!.insertBefore(wrapper, select);
      wrapper.appendChild(trigger);

      const positionMenu = () => {
        const rect = trigger.getBoundingClientRect();
        menu.style.top = `${rect.bottom + 4}px`;
        menu.style.left = `${rect.left}px`;
        menu.style.width = `${rect.width}px`;
      };

      const setOpen = (open: boolean) => {
        wrapper.classList.toggle('is-open', open);
        if (gridsterItem) {
          gridsterItem.classList.toggle('cpa-single-open', open);
        }
        if (open) {
          positionMenu();
          menu.classList.add('is-open');
        } else {
          menu.classList.remove('is-open');
        }
      };

      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const isOpen = wrapper.classList.contains('is-open');
        document.querySelectorAll('.cpa-single-select-fallback.is-open').forEach((el) => {
          if (el !== wrapper) el.classList.remove('is-open');
        });
        document.querySelectorAll('.cpa-single-select-menu-portal.is-open').forEach((el) => {
          if (el !== menu) el.classList.remove('is-open');
        });
        setOpen(!isOpen);
      });

      menu.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const target = (e.target as HTMLElement).closest('.cpa-single-select-option') as HTMLElement | null;
        if (!target) {
          return;
        }
        const value = target.getAttribute('data-value') || '';
        select.value = value;
        select.dispatchEvent(new Event('change', { bubbles: true }));

        const scope = this.angularJs.getScope();
        const ng = this.angularJs.getNg();
        if (scope && ng) {
          const ngModelCtrl = ng.element(select).controller('ngModel');
          if (ngModelCtrl) {
            scope.$apply(() => {
              ngModelCtrl.$setViewValue(value);
            });
          }
        }

        updateTriggerText();
        renderOptions();
        setOpen(false);
      });

      const outsideClickHandler = (e: Event) => {
        if (!wrapper.contains(e.target as Node) && !menu.contains(e.target as Node)) {
          setOpen(false);
        }
      };
      document.addEventListener('click', outsideClickHandler);
      this.documentClickHandlers.push(outsideClickHandler);

      (select as any).__cpaSingleFallbackRendered = true;
    });
  }

  renderMultipleFallbackChips(hostEl: HTMLElement | null | undefined): void {
    if (!hostEl) {
      return;
    }

    const jq = (window as any).$ || (window as any).jQuery;
    const hasSelect2 = !!(jq && jq.fn && typeof jq.fn.select2 === 'function');
    if (hasSelect2) {
      return;
    }

    const multiSelects = hostEl.querySelectorAll('select[multiple][bf-select2], select[multiple][data-bf-select2]');
    multiSelects.forEach((selectEl) => {
      const select = selectEl as HTMLSelectElement;
      const wrapper = select.closest('.cpa-select2-multiple');
      const gridsterItem = select.closest('.gridster-item') as HTMLElement | null;
      if (!wrapper) {
        return;
      }

      let container = wrapper.querySelector('.cpa-select2-fallback.select2-container.select2-container-multi') as HTMLElement | null;
      if (!container) {
        container = document.createElement('div');
        container.className = 'cpa-select2-fallback select2-container select2-container-multi';
        wrapper.insertBefore(container, select);
      }

      const selected = Array.from(select.selectedOptions).map((opt) => (opt.textContent || '').trim()).filter(Boolean);
      const availableOptions = Array.from(select.options)
        .filter((opt) => !opt.selected)
        .map((opt) => ({ value: opt.value, label: (opt.textContent || '').trim() }));

      container.innerHTML = buildMultiFallbackContainerHtml(selected, availableOptions);

      select.classList.add('cpa-multi-fallback-select');
      select.style.display = 'none';
      select.style.pointerEvents = 'none';
      if (!(select as any).__cpaFallbackBound) {
        select.addEventListener('change', () => this.renderMultipleFallbackChips(hostEl));
        (select as any).__cpaFallbackBound = true;
      }

      if (!(container as any).__cpaFallbackBound) {
        const setOpenState = (open: boolean) => {
          container!.classList.toggle('is-open', open);
          if (gridsterItem) {
            gridsterItem.classList.toggle('cpa-multi-open', open);
          }
        };

        container.addEventListener('click', (event: Event) => {
          const target = event.target as HTMLElement;
          const control = target.closest('.cpa-multi-fallback-control');
          const closeButton = target.closest('.select2-search-choice-close') as HTMLButtonElement | null;
          const optionButton = target.closest('.cpa-multi-fallback-option') as HTMLButtonElement | null;

          if (optionButton) {
            event.preventDefault();
            event.stopPropagation();
            const value = optionButton.getAttribute('data-option-value');
            const option = Array.from(select.options).find((opt) => opt.value === value);
            if (option) {
              option.selected = !option.selected;
              select.dispatchEvent(new Event('change', { bubbles: true }));
              setOpenState(false);
            }
            return;
          }

          if (closeButton) {
            event.preventDefault();
            event.stopPropagation();

            const chipLabel = closeButton.getAttribute('data-chip-label') || '';
            const optionToUnselect = Array.from(select.options).find((opt) => (opt.textContent || '').trim() === chipLabel);
            if (optionToUnselect) {
              optionToUnselect.selected = false;
              select.dispatchEvent(new Event('change', { bubbles: true }));
            }
            return;
          }

          if (control) {
            event.preventDefault();
            event.stopPropagation();
            setOpenState(!container!.classList.contains('is-open'));
          }
        });

        const outsideClickHandler = (event: Event) => {
          const target = event.target as HTMLElement;
          if (!container!.contains(target)) {
            setOpenState(false);
          }
        };
        document.addEventListener('click', outsideClickHandler);
        this.documentClickHandlers.push(outsideClickHandler);
        (container as any).__cpaFallbackBound = true;
      }
    });
  }

  /** Remove portais + listeners globais criados por esta instância (chamar no ngOnDestroy do host). */
  dispose(): void {
    this.portalMenus.forEach((m) => m.remove());
    this.portalMenus.length = 0;

    this.documentClickHandlers.forEach((handler) => document.removeEventListener('click', handler));
    this.documentClickHandlers.length = 0;
  }
}
